/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — export-pdf.ts
   exportPackagePDF(), PDF page building
   ═══════════════════════════════════════════════════════════════════════ */

import { state, type FirmProfile } from './state';
import { sum, rows, cur, meta, hasPriorData, computeNetIncome, extractYear } from './utils';
import { trackEvent } from './config';
import {
  mergeLabels,
  findNoncashItems,
  computeWorkingCapitalChanges,
  detectInvestingActivities,
  detectFinancingActivities,
  type CashFlowLineItem,
} from './statements';

/** Loads the firm profile from localStorage (matches auth.ts pattern). */
function loadFirmProfile(): FirmProfile {
  try {
    return JSON.parse(localStorage.getItem('noteflow-firmprofile-' + state.currentUserEmail) || '{}') || {};
  } catch { return {}; }
}

/**
 * Exports the full financial statement package as a multi-page PDF using jsPDF.
 */
export function exportPackagePDF(): void {
  try {
  trackEvent('pdf_exports');
  const jsPDFConstructor = (window as any).jspdf?.jsPDF || (window as any).jsPDF;
  const doc = new jsPDFConstructor({ unit: 'pt', format: 'letter' });
  const m = meta();
  const c = cur();
  const multi = hasPriorData();
  const pageW: number = doc.internal.pageSize.getWidth();
  const pageH: number = doc.internal.pageSize.getHeight();
  const margin = 54;
  const contentW = pageW - margin * 2;
  let pageNum = 0;
  const tocEntries: { title: string; page: number }[] = [];

  function fmtAmt(n: number | null | undefined): string {
    if (n === null || n === undefined) return '';
    const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n < 0 ? '(' + c + abs + ')' : c + abs;
  }

  function addFooter(d: any, pg: number): void {
    d.setFontSize(8);
    d.setTextColor(150);
    d.text(m.company + ' \u2014 Financial Statement Package', margin, pageH - 30);
    d.text('Page ' + pg, pageW - margin, pageH - 30, { align: 'right' });
    d.setTextColor(0);
  }

  function startPage(d: any, title: string): number {
    if (pageNum > 0) d.addPage();
    pageNum++;
    tocEntries.push({ title: title, page: pageNum });
    addFooter(d, pageNum);
    return pageNum;
  }

  // ── 1. COVER PAGE ──
  pageNum++;
  doc.setFillColor(30, 41, 59);
  doc.rect(0, 0, pageW, 200, 'F');
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 200, pageW, 6, 'F');

  doc.setTextColor(255);
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text(m.company, pageW / 2, 120, { align: 'center' });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Financial Statement Package', pageW / 2, 155, { align: 'center' });

  doc.setTextColor(30, 41, 59);
  doc.setFontSize(14);
  const pdfPriorYearOnly: string = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || m.priorPeriod || '';
  const pdfCurYear: string = (m.period ? extractYear(m.period) : null) || 'Current';
  const pdfPriorYear: string = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || 'Prior';
  const coverPeriod = multi && m.priorPeriod ? m.period + ' and ' + pdfPriorYearOnly : m.period;
  doc.text(coverPeriod, pageW / 2, 260, { align: 'center' });

  const pdfFirm = loadFirmProfile();
  let coverY = 290;

  if (m.engagementDate) {
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text('Engagement Date: ' + m.engagementDate, pageW / 2, coverY, { align: 'center' });
    coverY += 20;
  }

  doc.setFontSize(11);
  doc.setTextColor(100);
  const reportDateStr = m.reportDate || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  doc.text('Report Date: ' + reportDateStr, pageW / 2, coverY, { align: 'center' });
  coverY += 20;

  doc.text('Prepared with NoteFlow', pageW / 2, coverY, { align: 'center' });
  coverY += 30;

  if (pdfFirm.firmName) {
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.text(pdfFirm.firmName, pageW / 2, coverY, { align: 'center' });
    doc.setFont('helvetica', 'normal');
  }

  addFooter(doc, pageNum);

  // ── 2. TABLE OF CONTENTS ──
  doc.addPage();
  pageNum++;
  const tocPageNum = pageNum;

  // ── Helper: draw a financial statement table ──
  function drawStatementTable(title: string, dataRows: any[][], isTotalFn: (row: any[]) => boolean): void {
    startPage(doc, title);

    const isBS: boolean = title === 'Balance Sheet';
    const stmtPeriod: string = isBS
      ? (multi && m.priorPeriod ? m.period + ' and ' + pdfPriorYearOnly : m.period)
      : (multi && m.priorPeriod ? 'For the Year Ended ' + m.period + ' and ' + pdfPriorYearOnly : (m.period && m.period !== 'Period' ? 'For the Year Ended ' + m.period : m.period));

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(m.company, pageW / 2, margin + 10, { align: 'center' });
    doc.setFontSize(11);
    doc.text(title, pageW / 2, margin + 28, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(stmtPeriod, pageW / 2, margin + 44, { align: 'center' });
    doc.setTextColor(0);

    let head: string[][];
    let colStyles: Record<number, any>;
    if (multi) {
      head = [['', pdfCurYear, pdfPriorYear]];
      colStyles = {
        0: { cellWidth: contentW * 0.50, halign: 'left' },
        1: { cellWidth: contentW * 0.25, halign: 'right' },
        2: { cellWidth: contentW * 0.25, halign: 'right' }
      };
    } else {
      head = [['', 'Amount']];
      colStyles = {
        0: { cellWidth: contentW * 0.65, halign: 'left' },
        1: { cellWidth: contentW * 0.35, halign: 'right' }
      };
    }

    const body: any[][] = [];
    dataRows.forEach(function (row: any[]) {
      const labelStyle = isTotalFn(row) ? { fontStyle: 'bold' } : (String(row[0]).startsWith('  ') ? {} : { fontStyle: 'bold', textColor: [100, 116, 139] });
      const amtStyle = isTotalFn(row) ? { fontStyle: 'bold' } : {};
      const rowArr: any[] = [
        { content: row[0], styles: labelStyle },
        { content: row[1] !== undefined && row[1] !== '' ? fmtAmt(row[1]) : '', styles: amtStyle }
      ];
      if (multi) {
        rowArr.push({ content: row[2] !== undefined && row[2] !== '' ? fmtAmt(row[2]) : '', styles: amtStyle });
      }
      body.push(rowArr);
    });

    doc.autoTable({
      startY: margin + 60,
      head: head,
      body: body,
      theme: 'plain',
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, lineColor: [226, 232, 240], overflow: 'linebreak' },
      headStyles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 41, 59], lineWidth: { bottom: 0.5 } },
      columnStyles: colStyles,
      didDrawPage: function () {
        addFooter(doc, pageNum);
      },
      willDrawCell: function (data: any) {
        if (data.section === 'body') {
          const rowData = dataRows[data.row.index];
          if (rowData && isTotalFn(rowData)) {
            doc.setDrawColor(30, 41, 59);
            doc.setLineWidth(0.5);
            doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
          }
        }
      }
    });
  }

  function isTotal(row: any[]): boolean {
    const lbl = (String(row[0]) || '').trim();
    return lbl.indexOf('Total') === 0 || lbl.indexOf('TOTAL') === 0 ||
           lbl === 'Gross Profit' || lbl === 'Operating Income' || lbl === 'Net Income' ||
           lbl === 'Net Change in Cash';
  }

  // ── Helper functions for PDF sections ──
  function pushSection(arr: any[][], label: string, sectionKey: string): void {
    arr.push([label]);
    const items = mergeLabels(rows(state.currentData, sectionKey), multi ? rows(state.priorData, sectionKey) : [], multi);
    items.forEach(function (item) {
      if (multi) arr.push(['  ' + item.label, item.cur, item.prior]);
      else arr.push(['  ' + item.label, item.cur]);
    });
  }
  function pushTotal(arr: any[][], label: string, curVal: number, prior: number): void {
    if (multi) arr.push([label, curVal, prior]);
    else arr.push([label, curVal]);
  }

  // ── 3. BALANCE SHEET ──
  const bsAoa: any[][] = [];
  const totalCA = sum(state.currentData, 'current-assets'), totalNCA = sum(state.currentData, 'noncurrent-assets');
  const totalAssets = totalCA + totalNCA;
  const totalCL = sum(state.currentData, 'current-liab'), totalNCL = sum(state.currentData, 'noncurrent-liab');
  const pdfBsNI = computeNetIncome(state.currentData);
  const totalLiab = totalCL + totalNCL, totalEquity = sum(state.currentData, 'equity') + pdfBsNI;
  const totalLiabEquity = totalLiab + totalEquity;
  let pCA = 0, pNCA = 0, pAssets = 0, pCL = 0, pNCL = 0, pLiab = 0, pEq = 0, pLE = 0, pPdfBsNI = 0;
  if (multi) {
    pCA = sum(state.priorData, 'current-assets'); pNCA = sum(state.priorData, 'noncurrent-assets'); pAssets = pCA + pNCA;
    pCL = sum(state.priorData, 'current-liab'); pNCL = sum(state.priorData, 'noncurrent-liab'); pLiab = pCL + pNCL;
    pPdfBsNI = computeNetIncome(state.priorData);
    pEq = sum(state.priorData, 'equity') + pPdfBsNI; pLE = pLiab + pEq;
  }

  bsAoa.push(['ASSETS']);
  pushSection(bsAoa, 'Current Assets', 'current-assets');
  pushTotal(bsAoa, 'Total Current Assets', totalCA, pCA);
  pushSection(bsAoa, 'Non-Current Assets', 'noncurrent-assets');
  pushTotal(bsAoa, 'Total Non-Current Assets', totalNCA, pNCA);
  pushTotal(bsAoa, 'TOTAL ASSETS', totalAssets, pAssets);
  bsAoa.push(['']);
  bsAoa.push(['LIABILITIES & EQUITY']);
  pushSection(bsAoa, 'Current Liabilities', 'current-liab');
  pushTotal(bsAoa, 'Total Current Liabilities', totalCL, pCL);
  pushSection(bsAoa, 'Non-Current Liabilities', 'noncurrent-liab');
  pushTotal(bsAoa, 'Total Non-Current Liabilities', totalNCL, pNCL);
  pushTotal(bsAoa, 'Total Liabilities', totalLiab, pLiab);
  pushSection(bsAoa, "Shareholders' Equity", 'equity');
  if (multi) bsAoa.push(['  Net Income', pdfBsNI, pPdfBsNI]);
  else bsAoa.push(['  Net Income', pdfBsNI]);
  pushTotal(bsAoa, 'Total Equity', totalEquity, pEq);
  pushTotal(bsAoa, 'TOTAL LIABILITIES & EQUITY', totalLiabEquity, pLE);

  drawStatementTable('Balance Sheet', bsAoa, isTotal);

  // ── 5. INCOME STATEMENT ──
  const isAoa: any[][] = [];
  const totalRev = sum(state.currentData, 'revenue'), totalCOGS = sum(state.currentData, 'cogs');
  const grossProfit = totalRev - totalCOGS;
  const totalOpex = sum(state.currentData, 'opex'), opIncome = grossProfit - totalOpex;
  const totalOther = sum(state.currentData, 'other'), netIncome = opIncome + totalOther;
  let pRev = 0, pCOGS2 = 0, pGP = 0, pOpex = 0, pOpInc = 0, pOther = 0, pNI = 0;
  if (multi) {
    pRev = sum(state.priorData, 'revenue'); pCOGS2 = sum(state.priorData, 'cogs'); pGP = pRev - pCOGS2;
    pOpex = sum(state.priorData, 'opex'); pOpInc = pGP - pOpex;
    pOther = sum(state.priorData, 'other'); pNI = pOpInc + pOther;
  }

  pushSection(isAoa, 'Revenue', 'revenue');
  pushTotal(isAoa, 'Total Revenue', totalRev, pRev);
  pushSection(isAoa, 'Cost of Goods Sold', 'cogs');
  pushTotal(isAoa, 'Total Cost of Goods Sold', totalCOGS, pCOGS2);
  pushTotal(isAoa, 'Gross Profit', grossProfit, pGP);
  pushSection(isAoa, 'Operating Expenses', 'opex');
  pushTotal(isAoa, 'Total Operating Expenses', totalOpex, pOpex);
  pushTotal(isAoa, 'Operating Income', opIncome, pOpInc);
  if (rows(state.currentData, 'other').length || (multi && rows(state.priorData, 'other').length)) {
    pushSection(isAoa, 'Other Income / (Expense)', 'other');
    pushTotal(isAoa, 'Total Other Income / (Expense)', totalOther, pOther);
  }
  pushTotal(isAoa, 'Net Income', netIncome, pNI);

  drawStatementTable('Income Statement', isAoa, isTotal);

  // ── 6. STATEMENT OF CHANGES IN EQUITY ──
  startPage(doc, 'Statement of Changes in Equity');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(m.company, pageW / 2, margin + 10, { align: 'center' });
  doc.setFontSize(11);
  doc.text('Statement of Changes in Equity', pageW / 2, margin + 28, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);
  const eqPeriodLabel = multi && m.priorPeriod ? 'For the Year Ended ' + m.period + ' and ' + pdfPriorYearOnly : (m.period && m.period !== 'Period' ? 'For the Year Ended ' + m.period : m.period);
  doc.text(eqPeriodLabel, pageW / 2, margin + 44, { align: 'center' });
  doc.setTextColor(0);

  const eqBody: any[][] = [];
  if (multi) {
    eqBody.push([{ content: 'Beginning Balance (Prior Period)', styles: { fontStyle: 'bold' } }, fmtAmt(pEq)]);
    eqBody.push(['Net Income \u2014 Prior Period', fmtAmt(pNI)]);
    eqBody.push([{ content: 'Ending Balance (Prior Period)', styles: { fontStyle: 'bold' } }, fmtAmt(pEq)]);
    eqBody.push(['', '']);
    eqBody.push([{ content: 'Beginning Balance (Current Period)', styles: { fontStyle: 'bold' } }, fmtAmt(pEq)]);
    eqBody.push(['Net Income \u2014 Current Period', fmtAmt(netIncome)]);
    const eqItems = mergeLabels(rows(state.currentData, 'equity'), rows(state.priorData, 'equity'), multi);
    eqItems.forEach(function (item) {
      if (item.label.toLowerCase().indexOf('retained') === -1) {
        eqBody.push(['  ' + item.label, fmtAmt(item.cur)]);
      }
    });
    eqBody.push([{ content: 'Ending Balance (Current Period)', styles: { fontStyle: 'bold' } }, fmtAmt(totalEquity)]);
  } else {
    eqBody.push([{ content: 'Beginning Balance', styles: { fontStyle: 'bold' } }, fmtAmt(0)]);
    eqBody.push(['Net Income', fmtAmt(netIncome)]);
    const eqItems2 = rows(state.currentData, 'equity');
    eqItems2.forEach(function (item) {
      if (item.label.toLowerCase().indexOf('retained') === -1) {
        eqBody.push(['  ' + item.label, fmtAmt(parseFloat(String(item.amount)) || 0)]);
      }
    });
    eqBody.push([{ content: 'Ending Balance', styles: { fontStyle: 'bold' } }, fmtAmt(totalEquity)]);
  }

  doc.autoTable({
    startY: margin + 60,
    head: [['', 'Amount']],
    body: eqBody,
    theme: 'plain',
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, lineColor: [226, 232, 240] },
    headStyles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [30, 41, 59], lineWidth: { bottom: 0.5 } },
    columnStyles: { 0: { cellWidth: contentW * 0.65, halign: 'left' }, 1: { cellWidth: contentW * 0.35, halign: 'right' } },
    didDrawPage: function () { addFooter(doc, pageNum); }
  });

  // ── 7. CASH FLOW STATEMENT ──
  const cfAoa: any[][] = [];
  const cfNetIncome = computeNetIncome(state.currentData);
  const cfPNetIncome = multi ? computeNetIncome(state.priorData) : 0;
  const cfManualOpLabels = rows(state.currentData, 'cf-operating').map(function (r) { return r.label; });
  const cfPManualOpLabels = multi ? rows(state.priorData, 'cf-operating').map(function (r) { return r.label; }) : [];
  const cfNoncash = findNoncashItems(state.currentData, cfManualOpLabels);
  const cfPNoncash = multi ? findNoncashItems(state.priorData, cfPManualOpLabels) : [];
  const cfWC = multi ? computeWorkingCapitalChanges(state.currentData, state.priorData) : [];
  const cfAutoTotal = cfNoncash.reduce(function (s, i) { return s + i.amount; }, 0);
  const cfPAutoTotal = cfPNoncash.reduce(function (s, i) { return s + i.amount; }, 0);
  const cfWCTotal = cfWC.reduce(function (s, i) { return s + i.amount; }, 0);
  const cfManualOpTotal = sum(state.currentData, 'cf-operating');
  const cfPManualOpTotal = multi ? sum(state.priorData, 'cf-operating') : 0;
  const cfTotalOp = cfNetIncome + cfAutoTotal + cfWCTotal + cfManualOpTotal;
  const cfPOp = multi ? (cfPNetIncome + cfPAutoTotal + cfPManualOpTotal) : 0;
  const cfAutoInv: CashFlowLineItem[] = multi
    ? detectInvestingActivities(state.currentData, state.priorData) : [];
  const cfAutoFin: CashFlowLineItem[] = multi
    ? detectFinancingActivities(state.currentData, state.priorData) : [];
  const cfManualInv = mergeLabels(rows(state.currentData, 'cf-investing'), multi ? rows(state.priorData, 'cf-investing') : [], multi);
  const cfManualFin = mergeLabels(rows(state.currentData, 'cf-financing'), multi ? rows(state.priorData, 'cf-financing') : [], multi);
  const cfAutoInvTotal = cfAutoInv.reduce(function (s, i) { return s + i.amount; }, 0);
  const cfAutoFinTotal = cfAutoFin.reduce(function (s, i) { return s + i.amount; }, 0);
  const cfManualInvTotal = sum(state.currentData, 'cf-investing');
  const cfManualFinTotal = sum(state.currentData, 'cf-financing');
  const cfPManualInv = multi ? sum(state.priorData, 'cf-investing') : 0;
  const cfPManualFin = multi ? sum(state.priorData, 'cf-financing') : 0;
  const cfTotalInv = cfAutoInvTotal + cfManualInvTotal;
  const cfTotalFin = cfAutoFinTotal + cfManualFinTotal;
  const cfPInv = cfPManualInv;
  const cfPFin = cfPManualFin;
  const cfNetChange = cfTotalOp + cfTotalInv + cfTotalFin;
  const cfPNet = multi ? (cfPOp + cfPInv + cfPFin) : 0;

  cfAoa.push([{ content: 'Cash Flows from Operating Activities', styles: { fontStyle: 'bold' } }, '']);
  if (multi) { cfAoa.push(['  Net Income', fmtAmt(cfNetIncome), fmtAmt(cfPNetIncome)]); }
  else { cfAoa.push(['  Net Income', fmtAmt(cfNetIncome)]); }

  if (cfNoncash.length > 0 || (multi && cfPNoncash.length > 0)) {
    cfAoa.push([{ content: '  Adjustments for non-cash items:', styles: { fontStyle: 'italic' } }, '']);
    if (multi) {
      const allNc = mergeLabels(
        cfNoncash.map(function (i) { return { label: i.label, amount: i.amount }; }),
        cfPNoncash.map(function (i) { return { label: i.label, amount: i.amount }; }), true);
      allNc.forEach(function (item) { cfAoa.push(['    ' + item.label, fmtAmt(item.cur), fmtAmt(item.prior)]); });
    } else {
      cfNoncash.forEach(function (item) { cfAoa.push(['    ' + item.label, fmtAmt(item.amount)]); });
    }
  }
  if (cfWC.length > 0) {
    cfAoa.push([{ content: '  Changes in operating assets and liabilities:', styles: { fontStyle: 'italic' } }, '']);
    cfWC.forEach(function (item) {
      if (multi) { cfAoa.push(['    ' + item.label, fmtAmt(item.amount), '']); }
      else { cfAoa.push(['    ' + item.label, fmtAmt(item.amount)]); }
    });
  }
  const cfManualOp = mergeLabels(rows(state.currentData, 'cf-operating'), multi ? rows(state.priorData, 'cf-operating') : [], multi);
  if (cfManualOp.length > 0) {
    cfAoa.push([{ content: '  Other operating adjustments:', styles: { fontStyle: 'italic' } }, '']);
    cfManualOp.forEach(function (item) {
      if (multi) { cfAoa.push(['    ' + item.label, fmtAmt(item.cur), fmtAmt(item.prior)]); }
      else { cfAoa.push(['    ' + item.label, fmtAmt(item.cur)]); }
    });
  }
  pushTotal(cfAoa, 'Net Cash from Operating Activities', cfTotalOp, cfPOp);

  cfAoa.push(['Cash Flows from Investing Activities']);
  cfAutoInv.forEach(function (item) {
    if (multi) { cfAoa.push(['  ' + item.label, fmtAmt(item.amount), fmtAmt(item.priorAmount)]); }
    else { cfAoa.push(['  ' + item.label, fmtAmt(item.amount)]); }
  });
  cfManualInv.forEach(function (item) {
    if (multi) { cfAoa.push(['  ' + item.label, fmtAmt(item.cur), fmtAmt(item.prior)]); }
    else { cfAoa.push(['  ' + item.label, fmtAmt(item.cur)]); }
  });
  pushTotal(cfAoa, 'Net Cash from Investing Activities', cfTotalInv, cfPInv);
  cfAoa.push(['Cash Flows from Financing Activities']);
  cfAutoFin.forEach(function (item) {
    if (multi) { cfAoa.push(['  ' + item.label, fmtAmt(item.amount), fmtAmt(item.priorAmount)]); }
    else { cfAoa.push(['  ' + item.label, fmtAmt(item.amount)]); }
  });
  cfManualFin.forEach(function (item) {
    if (multi) { cfAoa.push(['  ' + item.label, fmtAmt(item.cur), fmtAmt(item.prior)]); }
    else { cfAoa.push(['  ' + item.label, fmtAmt(item.cur)]); }
  });
  pushTotal(cfAoa, 'Net Cash from Financing Activities', cfTotalFin, cfPFin);
  pushTotal(cfAoa, 'Net Change in Cash', cfNetChange, cfPNet);

  drawStatementTable('Statement of Cash Flows', cfAoa, isTotal);

  // ── 8. NOTES TO FINANCIAL STATEMENTS ──
  startPage(doc, 'Notes to the Financial Statements');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(m.company, pageW / 2, margin + 10, { align: 'center' });
  doc.setFontSize(11);
  doc.text('Notes to the Financial Statements', pageW / 2, margin + 28, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);
  const notesPeriodLabel = multi && m.priorPeriod ? 'For the Year Ended ' + m.period + ' and ' + pdfPriorYearOnly : (m.period && m.period !== 'Period' ? 'For the Year Ended ' + m.period : m.period);
  doc.text(notesPeriodLabel, pageW / 2, margin + 44, { align: 'center' });
  doc.setTextColor(0);

  const notesEl = document.getElementById('notes-statement');
  let notesText = '';
  if (notesEl && notesEl.innerText.trim()) {
    notesText = notesEl.innerText.trim();
  } else {
    notesText = 'Notes have not been generated yet. Complete the Notes questionnaire to include notes in this package.';
  }

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  const noteLines: string[] = doc.splitTextToSize(notesText, contentW);
  let noteY = margin + 68;
  noteLines.forEach(function (line: string) {
    if (noteY > pageH - 50) {
      doc.addPage();
      pageNum++;
      addFooter(doc, pageNum);
      noteY = margin + 20;
    }
    if (/^Note \d/.test(line.trim())) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      noteY += 6;
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60);
    }
    doc.text(line, margin, noteY);
    noteY += 14;
  });

  // ── NOW DRAW TABLE OF CONTENTS on page 2 ──
  doc.setPage(2);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Table of Contents', pageW / 2, margin + 30, { align: 'center' });

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let tocY = margin + 70;
  tocEntries.forEach(function (entry) {
    doc.setTextColor(30, 41, 59);
    doc.text(entry.title, margin + 10, tocY);
    doc.setTextColor(150);
    const titleW = doc.getStringUnitWidth(entry.title) * 11 / doc.internal.scaleFactor;
    const pageStr = String(entry.page);
    const pageStrW = doc.getStringUnitWidth(pageStr) * 11 / doc.internal.scaleFactor;
    const dotsStartX = margin + 10 + titleW + 8;
    const dotsEndX = pageW - margin - 10 - pageStrW - 4;
    const dotChar = '.';
    const dotW = doc.getStringUnitWidth(dotChar) * 11 / doc.internal.scaleFactor;
    const numDots = Math.floor((dotsEndX - dotsStartX) / (dotW + 1));
    if (numDots > 0) {
      doc.setTextColor(200);
      for (let d = 0; d < numDots; d++) {
        doc.text('.', dotsStartX + d * (dotW + 1), tocY);
      }
    }
    doc.setTextColor(30, 41, 59);
    doc.text(pageStr, pageW - margin - 10, tocY, { align: 'right' });
    tocY += 28;
  });

  addFooter(doc, tocPageNum);

  // ── Save ──
  const filename = (m.company || 'Financial_Statements').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_') + '_Package.pdf';
  doc.save(filename);
  } catch (err) {
    console.error('PDF export failed:', err);
    alert('Export failed. Please try again.');
  }
}
